// 分析页面

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Flex, Spin, Divider, Typography, Button, message } from "antd";
import { FileTextOutlined, DownloadOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import LineChartView from "./utils/LineChartView";
import { fetchAnaLineData } from "./utils/chartData";
import { downloadFile } from "../../NetComponents/downloadFile";
import RecopenIcon from "../../Common/Recopen/RecopenIcon";

const { Title, Paragraph, Text } = Typography;
const AnaPage = ({ decodeResult, handleCurrentStepReset, fileName }) => {
    // 分析完成状态
    const [isFinished, setIsFinished] = useState(true);
    // 分析结果
    const [result, setResult] = useState({});

    // 用于引用要导出的 DOM 区域
    const pdfContentRef = useRef(null);

    // 请求接口
    // ✅ 用 useCallback 包裹 fetchData，确保函数引用稳定
    const fetchData = useCallback(async () => {
        setIsFinished(false);
        try {
            const response = await fetchAnaLineData({
                x_label: decodeResult.x_label,
                y1_label: decodeResult.y1_label,
                y2_label: decodeResult.y2_label,
                x_data: decodeResult.x_data,
                y1_data: decodeResult.y1_data,
                y2_data: decodeResult.y2_data,
                time_interval: decodeResult.time_interval,
                file_name: fileName,
            });
            setResult(response);
        } catch (error) {
            console.error('❌ 请求失败:', error);
            message.error('获取分析数据失败，请重试');
        } finally {
            setIsFinished(true);
        }
    }, [decodeResult, fileName]); // ✅ 依赖所有可能变化的值

    // 进入界面开始请求接口解析
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // 导出html文件
    const exportHtml = () => {
        const fileName = result?.line_file;
        if (!fileName) {
            message.error('未找到可下载的文件');
            return;
        }
        downloadFile(fileName);
    };

    // 👇 新增：导出为 PDF
    const exportToPDF = async () => {
        if (!pdfContentRef.current) {
            message.error("未找到可导出的内容");
            return;
        }

        try {
            message.loading("正在生成 PDF...", 0);

            // 等待 ECharts 渲染完成（可选：加一点延迟）
            await new Promise(resolve => setTimeout(resolve, 500));

            const canvas = await html2canvas(pdfContentRef.current, {
                useCORS: true,
                allowTaint: true,
                scale: 2, // 提高清晰度
                logging: false,
                backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "px",
                format: [canvas.width, canvas.height],
            });

            pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
            // 日期
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0'); // 月份从0开始，需+1
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');

            const formattedDate = `${year}${month}${day}${hours}${minutes}`;
            pdf.save(`舒效比分析_${fileName}_${formattedDate}.pdf`);

            message.destroy();
            message.success("PDF 导出成功！");
        } catch (error) {
            message.destroy();
            console.error("PDF 导出失败:", error);
            message.error("PDF 导出失败，请重试");
        }
    };

    return (
        <div style={{ width: '100%' }}>
            {isFinished === false && (
                <Spin tip="分析中..." size="large" style={{ width: '100%', height: '100%' }}>
                    <div style={{ padding: 50, borderRadius: 4 }} />
                </Spin>
            )}

            {isFinished === true && (
                // 关键：包裹所有要导出的内容，并绑定生成PDF的ref
                <div ref={pdfContentRef} id="pdf-content" style={{ width: '100%', padding: '20px' }}>
                    <Title level={3}>分析结果</Title>
                    <Paragraph>
                        以下基于
                        <Text strong> {result?.ana_result?.line?.y1_label || decodeResult.y1_label}</Text>
                        <Text> 和 </Text>
                        <Text strong>{result?.ana_result?.line?.y2_label || decodeResult.y2_label}</Text>
                        <Text> 随 </Text>
                        <Text strong>{result?.ana_result?.line?.x_label || decodeResult.x_label}</Text> 的变化进行相关性分析
                    </Paragraph>

                    <Divider orientation="left" style={{ marginTop: 30 }}>相关性曲线图</Divider>
                    <Text type="secondary" italic='true'>
                        注意：该相关性数值已依据当前数据进行动态缩放，不同结果数据间的比较可能会受到影响。
                    </Text>

                    {/* 图表组件 */}
                    <div style={{ marginTop: 16 }}>
                        <LineChartView
                            x_label={result?.ana_result?.line?.x_label || decodeResult.x_label}
                            y1_label={result?.ana_result?.line?.y1_label || decodeResult.y1_label}
                            y2_label={result?.ana_result?.line?.y2_label || decodeResult.y2_label}
                            x_data={result?.ana_result?.line?.x_data || []}
                            y1_data={result?.ana_result?.line?.y1_data || []}
                            y2_data={result?.ana_result?.line?.y2_data || []}
                        />
                    </div>

                    {/* 相关性计算 */}
                    <Divider orientation="left" style={{ marginTop: 30 }}>🧪 相关性计算</Divider>
                    <Paragraph>
                        <Text italic>1.相关值：</Text>
                        <Text strong>{result?.ana_result?.correlation?.correlation_value || '--'}</Text>
                    </Paragraph>
                    <Paragraph italic>2.详情：</Paragraph>
                    <Paragraph strong>{result?.ana_result?.correlation?.description || '暂无'}</Paragraph>
                    <Paragraph italic>3.二次校验建议：</Paragraph>
                    <Paragraph strong>{result?.ana_result?.correlation?.comment || '暂无'}</Paragraph>

                    {/* 幅度计算 */}
                    <Divider orientation="left" style={{ marginTop: 30 }}>🔹 幅度计算</Divider>
                    <Paragraph strong>{result?.ana_result?.line?.y1_label || decodeResult.y1_label}</Paragraph>
                    <Paragraph>
                        <Text italic>1.最大值：</Text>
                        <Text strong>{result?.ana_result?.amplitude?.y1?.max || '--'}℃</Text>
                    </Paragraph>
                    <Paragraph>
                        <Text italic>2.最小值：</Text>
                        <Text strong>{result?.ana_result?.amplitude?.y1?.min || '--'}℃</Text>
                    </Paragraph>
                    <Paragraph>
                        <Text italic>3.振幅：</Text>
                        <Text strong>{result?.ana_result?.amplitude?.y1?.amplitude || '--'}℃</Text>
                    </Paragraph>

                    <Paragraph strong>{result?.ana_result?.line?.y2_label || decodeResult.y2_label}</Paragraph>
                    <Paragraph>
                        <Text italic>1.最大值：</Text>
                        <Text strong>{result?.ana_result?.amplitude?.y2?.max || '--'}℃</Text>
                    </Paragraph>
                    <Paragraph>
                        <Text italic>2.最小值：</Text>
                        <Text strong>{result?.ana_result?.amplitude?.y2?.min || '--'}℃</Text>
                    </Paragraph>
                    <Paragraph>
                        <Text italic>3.振幅：</Text>
                        <Text strong>{result?.ana_result?.amplitude?.y2?.amplitude || '--'}℃</Text>
                    </Paragraph>

                    {/* 周期计算 */}
                    <Divider orientation="left" style={{ marginTop: 30 }}>♻️ 周期计算</Divider>
                    <Paragraph>{result?.ana_result?.period || '暂无计算结果'}</Paragraph>

                    {/* 拓扑等效K值 */}
                    <Divider orientation="left" style={{ marginTop: 30 }}>🌟 拓扑等效K值</Divider>
                    <Paragraph>
                        <Text italic>1.拓扑等效K值：</Text>
                        <Text strong>{result?.ana_result?.topology?.value || '--'} %</Text>
                    </Paragraph>
                    <Paragraph>
                        <Text italic>2.理想的参考回水温度(K=0.7)：</Text>
                        <Text strong>{result?.ana_result?.topology?.ideal_return_temp || '--'}℃</Text>
                    </Paragraph>
                    <Paragraph>
                        <Text italic>3.最佳的参考回水温度(K=0.5)：</Text>
                        <Text strong>{result?.ana_result?.topology?.best_return_temp || '--'}℃</Text>
                    </Paragraph>
                    <Paragraph italic>4.详情：</Paragraph>
                    <Paragraph strong>{result?.ana_result?.topology?.description || '暂无'}</Paragraph>
                    {/* 舒效比 */}
                    <Divider orientation="left" style={{ marginTop: 30 }}><RecopenIcon /> 舒效比</Divider>
                    <Paragraph>
                        <Text italic>1.热惰性：</Text>
                        <Text strong>{result?.ana_result?.thermal_lag?.value || '--'}</Text>
                    </Paragraph>
                    <Paragraph><blockquote>参数说明：数值越小，热惰性越强</blockquote></Paragraph>
                    <Paragraph>
                        <Text italic>2.舒效比：</Text>
                        <Text strong>{result?.ana_result?.recopen?.value || '--'}</Text>
                    </Paragraph>
                    <Paragraph italic>3.详情：</Paragraph>
                    <Paragraph strong>{result?.ana_result?.recopen?.description || '暂无'}</Paragraph>

                    <Divider plain variant="dashed" style={{ marginTop: 50, color: '#999' }}>
                        v{result?.ana_result?.version || '0.0.0'}
                    </Divider>
                </div>
            )}

            {/* 按钮区域（放在内容外，避免被截图） */}
            {isFinished && (
                <Flex justify="center" gap='large' style={{ width: '100%', marginTop: 30, marginBottom: 30 }}>
                    <Button onClick={handleCurrentStepReset}>重新计算</Button>
                    <Button color="primary" variant="outlined" icon={<FileTextOutlined />} onClick={exportHtml}>
                        导出为HTML文件
                    </Button>
                    {/* 👇 新增 PDF 按钮 */}
                    <Button color="primary" variant="outlined" icon={<DownloadOutlined />} onClick={exportToPDF}>
                        导出为PDF文件
                    </Button>
                </Flex>
            )}
        </div>
    );
};

export default AnaPage;